<template>
  <div class="file-uploader-container">
    <!-- Área de drag and drop -->
    <div
      v-if="!uploadedFile"
      class="upload-area"
      :class="{ 'drag-over': isDragOver }"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      @click="triggerFileInput"
    >
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        @change="onFileSelect"
        style="display: none"
      />

      <div class="upload-content">
        <div class="upload-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17,8 12,3 7,8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
        </div>
        <div class="upload-text">
          {{ dragDropText }}
        </div>
        <div class="file-types">
          {{ fileTypesText }}
        </div>
      </div>
    </div>

    <!-- Archivo cargado -->
    <div v-if="uploadedFile" class="file-item" :class="{ 'has-error': hasErrorMessage }">
      <div class="file-icon">
        <!-- Icono de error si hay error -->
        <svg v-if="hasErrorMessage" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="2" width="18" height="20" rx="2" fill="#ef4444"/>
          <path d="M7 6h10M7 10h10M7 14h7" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        <!-- Icono normal si no hay error -->
        <img
          v-else
          src="@/assets/images/svg/icon-microsoft-excel.svg"
          width="24"
          height="24"
          alt="Excel file icon"
        />
      </div>

      <div class="file-info">
        <div class="file-name">{{ uploadedFile.name }}</div>
        <div class="file-details">
          <span class="file-size">{{ formatBytes(uploadedFile.size) }}</span>
          <span v-if="hasErrorMessage" class="error-status">{{ errorStatusText }}</span>
          <span v-else-if="uploadProgress < 100" class="upload-status">{{ uploadStatusText }}</span>
        </div>

        <!-- Barra de progreso solo si no hay error -->
        <div v-if="!hasErrorMessage && uploadProgress < 100" class="progress-bar">
          <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
        </div>
      </div>

      <div v-if="!hasErrorMessage && uploadProgress < 100" class="upload-percentage">
        {{ Math.round(uploadProgress) }}%
      </div>

      <button class="remove-file-btn" @click="removeUploaderFile">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>

    <!-- Mensaje de error debajo del archivo -->
    <div v-if="uploadedFile && hasErrorMessage" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { formatBytes } from '@/helpers/file'

interface Props {
  accept?: string
  maxFileSize?: number // en bytes
  dragDropText?: string
  fileTypesText?: string
  uploadStatusText?: string
  errorStatusText?: string
  errorMessage?: string
}

interface Emits {
  (e: 'fileSelected', file: File): void
  (e: 'fileRemoved'): void
  (e: 'uploadProgressComplete'): void
  (e: 'fileError', error: string): void
}

const props = withDefaults(defineProps<Props>(), {
  accept: '.xlsx',
  maxFileSize: 10 * 1024 * 1024, // 10MB por defecto
  dragDropText: 'Arrastra un único documento o explora tu dispositivo',
  fileTypesText: 'Archivos válidos: .xlsx',
  uploadStatusText: 'Subiendo...',
  errorStatusText: 'Error',
  errorMessage: ''
})

const emit = defineEmits<Emits>()

// Estado reactivo
const fileInput = ref<HTMLInputElement>()
const isDragOver = ref(false)
const uploadedFile = ref<File | null>(null)
const uploadProgress = ref(0)
const hasErrorMessage = computed(() => props.errorMessage)

// Métodos para drag and drop
function onDragOver(event: DragEvent) {
  event.preventDefault()
  isDragOver.value = true
}

function onDragLeave(event: DragEvent) {
  event.preventDefault()
  isDragOver.value = false
}

function onDrop(event: DragEvent) {
  event.preventDefault()
  isDragOver.value = false

  const files = event.dataTransfer?.files
  if (files && files.length > 0) {
    handleFileSelection(files[0])
  }
}

function triggerFileInput() {
  fileInput.value?.click()
}

function onFileSelect(event: Event) {
  const target = event.target as HTMLInputElement
  const files = target.files
  if (files && files.length > 0) {
    handleFileSelection(files[0])
  }
}

function handleFileSelection(file: File) {
  uploadedFile.value = file
  uploadProgress.value = 0

  // Si no hay error, simular progreso de subida
  simulateUploadProgress()

  // Emitir evento
  emit('fileSelected', file)
}

function simulateUploadProgress() {
  const interval = setInterval(() => {
    uploadProgress.value += Math.random() * 15 + 5

    if (uploadProgress.value >= 100) {
      uploadProgress.value = 100
      emit('uploadProgressComplete', uploadProgress.value)
      clearInterval(interval)
    }
  }, 200)
}

function removeUploaderFile(): void {
  uploadedFile.value = null
  uploadProgress.value = 0

  // Limpiar el input
  if (fileInput.value) {
    fileInput.value.value = ''
  }

  emit('fileRemoved')
}

</script>

<style lang="scss" scoped>
.file-uploader-container {
  width: 100%;

  .upload-area {
    border: 2px dashed #d1d5db;
    border-radius: 8px;
    padding: 48px 24px;
    text-align: center;
    background-color: #f9fafb;
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover,
    &.drag-over {
      border-color: #3b82f6;
      background-color: #f0f9ff;
    }

    .upload-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;

      .upload-icon {
        color: #9ca3af;

        svg {
          width: 32px;
          height: 32px;
        }
      }

      .upload-text {
        color: #374151;
        font-size: 14px;
        font-weight: 500;
        line-height: 1.4;
      }

      .file-types {
        color: #6b7280;
        font-size: 12px;
      }
    }
  }

  .file-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background-color: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

    .file-icon {
      flex-shrink: 0;

      svg {
        width: 24px;
        height: 24px;
      }
    }

    .file-info {
      flex: 1;
      min-width: 0; // Para permitir truncamiento

      .file-name {
        font-weight: 500;
        color: #374151;
        font-size: 14px;
        margin-bottom: 4px;
        word-break: break-word;
      }

      .file-details {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 8px;

        .file-size {
          color: #6b7280;
          font-size: 12px;
        }

        .upload-status {
          color: #6b7280;
          font-size: 12px;
        }

        .error-status {
          color: #ef4444;
          font-size: 12px;
          font-weight: 500;
        }
      }

      .progress-bar {
        width: 100%;
        height: 4px;
        background-color: #e5e7eb;
        border-radius: 2px;
        overflow: hidden;

        .progress-fill {
          height: 100%;
          background-color: #16a34a;
          transition: width 0.3s ease;
          border-radius: 2px;
        }
      }
    }

    .upload-percentage {
      flex-shrink: 0;
      color: #16a34a;
      font-size: 12px;
      font-weight: 500;
      margin-right: 8px;
    }

    .remove-file-btn {
      flex-shrink: 0;
      background: none;
      border: none;
      color: #9ca3af;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: all 0.2s ease;

      &:hover {
        color: #ef4444;
        background-color: #fef2f2;
      }

      svg {
        width: 16px;
        height: 16px;
      }
    }

    &.has-error {
      border-color: #fecaca;
      background-color: #fef2f2;
    }
  }

  .error-message {
    margin-top: 8px;
    color: #ef4444;
    font-size: 12px;
    font-weight: 500;
  }
}
</style>
